import { useSessionId } from '@payments-checkout/shared/providers';
import { UseMutationOptions, useMutation } from '@tanstack/react-query';
import { useParams } from 'next/navigation';
import { useFormContext } from 'react-hook-form';
import {
  buildBillingAddress,
  getBillingContactName,
} from '../../helpers/billingContact';
import { setCustomerData } from '../../../hooks/useCustomerData/useCustomerData';
import { buildPaymentPayload } from '../../../hooks/usePayment/buildPaymentPayload';
import { sendPayment } from '../../../hooks/usePayment/usePayment.hook';
import { useApplePayReducedForm } from '../../../hooks/useApplePayReducedForm';
import { useSendBillingAddress } from '../../../hooks/useSendBillingAddress/useSendBillingAddress';

type UseApplePaymentMutationProps = {
  paymentData: any;
  appleDetails: any;
};

export const useApplePayPayment = (
  options?: Omit<
    UseMutationOptions<any, Error, UseApplePaymentMutationProps, unknown>,
    'mutateFn'
  >
) => {
  const { paymentLinkId } = useParams<{ paymentLinkId: string }>();
  const { sessionId } = useSessionId();
  const form = useFormContext();
  const { mutateAsync: sendBillingAddress } = useSendBillingAddress();
  const isReducedForm = useApplePayReducedForm();

  return useMutation({
    mutationFn: async ({
      paymentData,
      appleDetails,
    }: UseApplePaymentMutationProps) => {
      if (isReducedForm) {
        await setCustomerData({
          paymentLinkId,
          sessionId: sessionId as string,
          customerData: {
            name: getBillingContactName(appleDetails?.payment?.billingContact),
          },
        });
      } else {
        await sendBillingAddress(buildBillingAddress(form.getValues()));
      }

      const paymentInfo = buildPaymentPayload('apple_pay', {
        details: { apple_pay: paymentData },
      });

      return sendPayment(paymentLinkId, paymentInfo, sessionId);
    },
    ...options,
  });
};
